/**
 * This module is a Very Light™ wrapper on top of the HTML5 history API.
 * At a high level, this API is responsible for navigating directly to a
 * new location, traversing the history stack (e.g. `go(-1)` to go back
 * a single entry) and keeping track of the current location.
 *
 * The primary export here is a factory which creates a `history` instance.
 *
 * A history instance has a few properties/methods:
 *
 * * subscribe: An event-emitter style subscription callback. Returns a
 *   cleanup function.
 *
 * * navigate: The function to navigate to a discrete location.
 *
 * * redirect: A function to clobber the existing location with a new one.
 *
 * * getRedirectMessage: Retieve the app-provided message for redirecting.
 *
 * * addConfirmation: A function to potentially prompt a user prior to
 *   navigation.
 *
 * * getReferrer: A function to read the latest referrer value.
 *
 * * overrideBackDestination: A function to clobber the destination of going
 *   back by a single entry.
 *
 * * goBack: A function to re-hydrate state from the previous entry.
 *
 * * location: The struct representing the current entry in the history
 *   stack. This includes most of what's in `window.location`, in addition
 *   to a `key` property. The `key` property is a unique identifier that is
 *   persisted across page refreshes.
 */

import {
  ensureTrailingSlashWhenSearchIsEmpty,
  pathFromHref,
  removeTrailingSlash,
  searchParamsFromSearchString,
} from './url';

function createHistoryKey() {
  return Date.now() + '';
}

function getCurrentLocation(type: $TSFixMe) {
  const { state } = window.history;

  const location = window.location;
  let pathname = location.pathname;

  // Handle the development-only edge case of a link containing
  // `/dev.php` by removing it prior to matching.
  if (__DEV__) {
    pathname = pathname.replace(/\/dev.php/, '');
  }

  return {
    ...location,
    pathname: removeTrailingSlash(pathname),
    searchParams: searchParamsFromSearchString(location.search),
    // We rely on `history.state` to persist some data across page
    // refreshes and navigations. By default, or, say, when an app
    // is initially loading, the value of `history.state` resolves
    // to null. We paper over this detail by providing some basic
    // fallback values.
    key: state ? state.key : createHistoryKey(),
    // Fall back to an index of 0
    index: state ? state.index : 0,
    type,
  };
}

/**
 * The history factory. This function creates a wrapped history
 * instance, which mimics the APIs of an EventEmitter.
 *
 * @param {Object} opts
 * @param {Function} opts.isPathNavigable A function to determine if a
 * given path is navigable. We use this in order to handle outbound
 * links (i.e., paths that point to addresses in other clients).
 * @return {Object} The history instance
 */
export default function createHistory({
  isPathNavigable = () => true,
  referrer = document.referrer,
} = {}) {
  let listeners: $TSFixMe = [];
  let confirmations: $TSFixMe = [];
  let currentIndex = (history.state && history.state.index) || 0;
  let restoringPopstateWithInversePopstate = false;
  let backButtonOverride: $TSFixMe;

  // We might care to tell the user why we've redirected. As such,
  // we will store an arbitrary value from upstream calls to `redirect`,
  // exposing the value behind `getRedirectMessage`.
  let redirectMessage: $TSFixMe;

  // Add a popstate handler. This accounts for hashchanges, as well
  // as navigating via the back and forward buttons (or history APIs).
  // There's some additional handling here in order to support
  // unwinding potentially destructive navigations (e.g. pressing the
  // back button when you have a form filled out).
  window.addEventListener('popstate', (e) => {
    // Popstate events are not cancelable, nor are they confirmable.
    // We can synthetically confirm a navigation by verifying the
    // confirmation callbacks registered, then unwinding any offset
    // transitions on falsy confirmations.
    if (restoringPopstateWithInversePopstate) {
      restoringPopstateWithInversePopstate = false;
      return;
    }

    const popstateIndex = e.state && e.state.index;

    if (typeof popstateIndex === 'number') {
      if (userIsPromptedAndDoesNotWantToNavigate()) {
        restoringPopstateWithInversePopstate = true;
        history.go(currentIndex - popstateIndex);
        return;
      }
      // If the user didn't block the navigation, re-sync the index.
      currentIndex = popstateIndex;
    } else {
      // If the popstate event didn't have an index, re-sync it to 0.
      currentIndex = 0;
    }

    emit('POP');
  });

  // Add a beforeunload handler in order to coordinate confirmation
  // messages before navigations occur.
  window.addEventListener('beforeunload', (e) => {
    const confirmationMessage = getConfirmationMessage();

    if (confirmationMessage !== undefined) {
      e.returnValue = confirmationMessage;
      return confirmationMessage;
    }
  });

  // Iterate over the attached confirmation handlers, checking to
  // see if any of them want to prompt the user. Should one return
  // a value, we use that.
  function getConfirmationMessage() {
    let message;

    for (let i = confirmations.length; i > 0; i--) {
      message = confirmations[i - 1]();
      if (message !== undefined) {
        break;
      }
    }

    return message;
  }

  // The event emitter portion of history. Calls all handlers with
  // the most recent version of the location.
  function emit(type: $TSFixMe) {
    const location = getCurrentLocation(type);
    // @ts-expect-error TS(7006): Parameter 'listener' implicitly has an 'any' type.
    listeners.forEach((listener) => {
      listener(location);
    });
  }

  /**
   * The subscription API for the history instance. Behaves much
   * like an EventEmitter.
   *
   * @param {Function} fn The callback to invoke upon navigations
   * @return {Function} A handle to detach the event handler
   */
  function subscribe(fn: $TSFixMe) {
    listeners.push(fn);

    return function unsubscribe() {
      // @ts-expect-error TS(7006): Parameter 'listener' implicitly has an 'any' type.
      listeners = listeners.filter((listener) => {
        return listener !== fn;
      });
    };
  }

  /**
   * Adds a confirmation to the confirmation stack. The expectation
   * here is that a confirmation is a callable which conditionally
   * returns a string. The string is used downstream, in order to
   * inform the user of potential side-effects from navigating (e.g.
   * clicking the back button after filling out but not yet submitting
   * a form).
   *
   * @param {Function} fn The confirmation callback
   * @return {Function} A handle to detach ones confirmation
   */
  function addConfirmation(fn: $TSFixMe) {
    confirmations.push(fn);

    return function removeConfirmation() {
      // @ts-expect-error TS(7006): Parameter 'confirmation' implicitly has an 'any' t... Remove this comment to see the full error message
      confirmations = confirmations.filter((confirmation) => {
        return confirmation !== fn;
      });
    };
  }

  // A helper function to handle the user confirmation lifecycle.
  // This is called prior to any navigation calls, as well as in
  // the popstate handler.
  function userIsPromptedAndDoesNotWantToNavigate() {
    const confirmationMessage = getConfirmationMessage();

    if (confirmationMessage !== undefined) {
      return confirm(confirmationMessage) !== true;
    }

    return false;
  }

  // A function to return the previous address of the application.
  // When initially loading the page, this value falls back to
  // `document.referrer`, and when _that_ doesn't exist, falls back
  // to `/`. Otherwise, we'll pull the value from `history.state`.
  function getReferrer() {
    const { state } = window.history;

    // If the user is directly loading the page and `history.state` has
    // not yet been set, or the referrer value is missing, fallback to
    // the root path.
    if (state === null || state.referrer === undefined) {
      return referrer || '/';
    }

    return state.referrer;
  }

  /**
   * Begins a navigation transition, sending the user to a given
   * link. By default, navigations are PUSH events, but can be
   * specified to REPLACE instead.
   *
   * This function also accounts for confirmation a navigation,
   * normalizing the given href value, creating the next history
   * state instance and forking on whether we navigate in-app or
   * trigger a new HTTP request.
   *
   * @param {String} to The destination
   * @param {Boolean} replace Whether to replace in lieu of push
   * @param {String} message A value indicating why we're redirecting
   */
  function performNavigation(to: string, replace = false, message?: string) {
    // Until the monolith is fully in TypeScript, we should consider incorrect types potentially making it through
    // We also treat empty strings to not redirect the user
    if (
      userIsPromptedAndDoesNotWantToNavigate() ||
      typeof to !== 'string' ||
      !to
    ) {
      return;
    }

    // TODO: AF Investigation
    // We need to investigate moving call to sandbox from useNavigate
    // to this function where we can explore manipulating the custom
    // history state.
    try {
      to = ensureTrailingSlashWhenSearchIsEmpty(to);

      // @ts-expect-error TS(2554): Expected 0 arguments, but got 1.
      if (!isPathNavigable(pathFromHref(to))) {
        window.location.assign(to);
        return;
      }

      const state = {
        key: createHistoryKey(),
        index: ++currentIndex,
        // Persist the current location as the referrer for the next entry.
        referrer: window.location.href,
      };

      if (!replace) {
        redirectMessage = undefined;
        // @ts-expect-error TS(2345): Argument of type 'null' is not assignable to param... Remove this comment to see the full error message
        window.history.pushState(state, null, to);
      } else {
        // We don't care to hang onto empty messages
        if (redirectMessage !== '') {
          redirectMessage = message;
        }
        // @ts-expect-error TS(2345): Argument of type 'null' is not assignable to param... Remove this comment to see the full error message
        window.history.replaceState(state, null, to);
      }

      emit(replace ? 'REPLACE' : 'PUSH');
    } catch {
      // eslint-disable-next-line no-useless-return
      return;
    }
  }

  /**
   * Kicks off a client-side navigation.
   *
   * @param {String} to The destination URL
   */
  function navigate(to: string) {
    performNavigation(to);
  }

  /**
   * Kicks off a client-side redirect.
   *
   * @param {String} to The destination URL
   * @param {String?} message A message for redirecting
   */
  function redirect(to: string, message?: string) {
    performNavigation(to, true, message);
  }

  /**
   * Sets a custom back button destination and returns a thunk function
   * to restore the default `goBack` behaviour.
   *
   * @param {String} path
   */
  function overrideBackDestination(path: $TSFixMe) {
    backButtonOverride = path;

    return () => {
      backButtonOverride = null;
    };
  }

  /**
   * Goes back one entry in the stack. If a custom back destination has been
   * set, navigates to that path instead.
   */
  function goBack() {
    // If a custom destination has been set, navigate to that instead.
    if (backButtonOverride) {
      navigate(backButtonOverride);
      return;
    }
    // Otherwise, navigate to the last referred address
    navigate(getReferrer());
  }

  /**
   * Reads from the closure state.
   */
  function getRedirectMessage() {
    return redirectMessage;
  }

  // Our history wrapper doesn't expose the following methods and
  // properties that are normally a part of the history interface:
  //
  // * go(delta)
  // * forward()
  // * length
  //
  // For the vast majority of use cases we will not need to access
  // any of these in our UI, either directly or indirectly. In the
  // exceedingly unlikely event we need to expose one or more of
  // these, we can, though we should hold out until that time comes.
  return {
    get location() {
      // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
      return getCurrentLocation();
    },
    subscribe,
    navigate,
    redirect,
    addConfirmation,
    getRedirectMessage,
    getReferrer,
    overrideBackDestination,
    goBack,
  };
}
